import React, { useState } from "react";
import { Skeleton } from "antd";
import "./style.scss";
import HalfDonutPieChart from "../../Reuseable/HalfDonutPieChart/HalfDonutPieChart";

const COLORS = ["#3B63FF", "#00C9A7", "#FF9F40", "#FF4C61"];

/* "0d 0h 4m 12s" spends most of its width on units that are zero. The leading
   zero units are dropped and the unit letters are set smaller than the
   figures, so the number that matters is the one you read first. */
const buildParts = ({ days, hours, minutes, seconds }) => {
  const all = [
    { value: days, unit: "d" },
    { value: hours, unit: "h" },
    { value: minutes, unit: "m" },
    { value: seconds, unit: "s" },
  ];
  const firstNonZero = all.findIndex((part) => Number(part.value) > 0);
  if (firstNonZero === -1) return [{ value: 0, unit: "s" }];
  return all.slice(firstNonZero);
};

const StatsCard = ({ title = "Elapsed Time", time, footer = true, loading = false }) => {
  const [selected, setSelected] = useState(null);

  const { days = 0, hours = 0, minutes = 0, seconds = 0 } = time || {};

  const fullChart = [
    { name: "Days", value: days },
    { name: "Hours", value: hours },
    { name: "Minutes", value: minutes },
    { name: "Seconds", value: seconds },
  ];

  // handle legend click
  const handleLegendClick = (index) => {
    setSelected((current) => (current === index ? null : index));
  };

  // 🟢 Chart filtering logic
  const displayedChartData =
    selected !== null
      ? [{ name: fullChart[selected].name, value: fullChart[selected].value }]
      : fullChart;

  if (loading) {
    return (
      <article className="summaryCard is-loading">
        <Skeleton active paragraph={{ rows: 2 }} title={{ width: "45%" }} />
      </article>
    );
  }

  return (
    <article className="summaryCard">
      <header className="summaryCard__head">
        <span className="summaryCard__title">{title}</span>
      </header>

      <div className="summaryCard__body">
        <div className="summaryCard__readout">
          {selected !== null ? (
            <span className="summaryCard__value">
              {fullChart[selected].value}
              <i>{fullChart[selected].name}</i>
            </span>
          ) : (
            <span className="summaryCard__value">
              {buildParts({ days, hours, minutes, seconds }).map((part) => (
                <span className="summaryCard__part" key={part.unit}>
                  {part.value}
                  <i>{part.unit}</i>
                </span>
              ))}
            </span>
          )}
        </div>

        <div className="summaryCard__chart">
          {/* HalfDonutPieChart is a plain function component - it takes no ref
              and no selectedIndex, so the highlightSlice() call that used to
              sit here never ran and only produced a ref warning. Selection is
              driven by the data and colours passed in. */}
          <HalfDonutPieChart
            data={displayedChartData}
            colors={selected !== null ? [COLORS[selected]] : COLORS}
            showValues={true}
            formatValue={(v) => v}
            height={80}
            width={140}
          />
        </div>
      </div>

      {footer && (
        <footer className="summaryCard__legend">
          {fullChart.map((item, index) => (
            <button
              type="button"
              key={item.name}
              className={`summaryCard__legendItem ${selected === index ? "is-active" : ""}`}
              onClick={() => handleLegendClick(index)}
              aria-pressed={selected === index}
            >
              <span className="dot" style={{ background: COLORS[index] }} />
              {item.name}
            </button>
          ))}
        </footer>
      )}
    </article>
  );
};

export default StatsCard;
